import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Check, Loader2 } from 'lucide-react';

export const ActivatePage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [, setSuccess] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const subscriptionId = queryParams.get('subscriptionId');
    const token = queryParams.get('token');

    // Nunca expõe detalhe técnico (erro de banco, stack, HTTP) para o usuário.
    // Se a mensagem do backend parecer técnica, cai numa mensagem amigável genérica.
    const FRIENDLY_FALLBACK =
      'Não conseguimos confirmar seu email agora. Tente novamente em alguns minutos ou fale com nosso suporte.';
    const sanitizeError = (msg?: string) => {
      if (!msg) return FRIENDLY_FALLBACK;
      const technical = /failed query|insert into|select |update |sqlite|constraint|http!|status:\s*\d|undefined|\{".*\}|stack|exception/i;
      return technical.test(msg) ? FRIENDLY_FALLBACK : msg;
    };

    const activateEmail = async () => {
      if (!subscriptionId) {
        setError('Subscription ID não fornecido.');
        setLoading(false);
        return;
      }

      try {
        // Construir a URL com os parâmetros necessários
        const url = new URL('https://waffle-v2.theo-110.workers.dev/referrals/activate');
        url.searchParams.set('subscriptionId', subscriptionId);
        
        // Adicionar token se fornecido
        if (token) {
          url.searchParams.set('token', token);
        }

        const response = await fetch(url.toString());
        
        if (response.ok) {
          setSuccess(true);
        } else if (response.status === 404) {
          setError('Assinatura não encontrada. Verifique o ID da assinatura.');
        } else {
          const contentType = response.headers.get("content-type");
          let errorData;

          if (contentType && contentType.includes("application/json")) {
            errorData = await response.json();
          } else {
            throw new Error('Falha na ativação do email. Resposta não é JSON.');
          }

          // Prioriza a mensagem amigável do backend (message) sobre o campo técnico (error).
          throw new Error(errorData.message || errorData.error || 'Falha na ativação do email.');
        }
      } catch (err: any) {
        setError(sanitizeError(err?.message));
        console.error('Erro detalhado:', err);
      } finally {
        setLoading(false);
      }
    };

    activateEmail();
  }, [location.search]);
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-gray-900 p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center"
        >
          <Loader2 className="w-8 h-8 text-yellow-400 animate-spin" />
          <p className="mt-4 text-gray-600 dark:text-gray-300">Ativando seu email...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-gray-900 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-4xl p-6"
        >
          <div className="bg-red-100 w-16 h-16 rounded-lg flex items-center justify-center mb-4">
            <span className="text-2xl">❌</span>
          </div>
          <h1 className="text-3xl font-bold mb-4 dark:text-white">Ops! Algo deu errado</h1>
          <p className="text-gray-600 dark:text-gray-300">{error}</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-gray-900 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-4xl flex flex-col lg:flex-row"
      >
        <div className="flex-1 p-6">
          <div className="mb-8">
            <div className="bg-yellow-400 w-16 h-16 rounded-lg flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-black dark:text-white" />
            </div>
            <div className="text-yellow-400 font-medium mb-2">the news ☕️</div>
            <h1 className="text-3xl font-bold mb-4 dark:text-white">
              email confirmado com sucesso! 🎉
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Agora é só aguardar nossas newsletters chegarem quentinhas na sua caixa de entrada.
            </p>
          </div>
        </div>

        <div className="flex-1 p-6 hidden lg:block">
          <Card className="bg-white dark:bg-gray-800 shadow-lg rounded-xl overflow-hidden">
            <CardContent className="p-6">
              {/* Conteúdo similar ao card de preview do SubscribePage */}
              {/* ... */}
            </CardContent>
          </Card>
        </div>
      </motion.div>
    </div>
  );
}; 